import React, { useState, useRef, useEffect } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import axios from 'axios';
import config from '../../config.js';
import { store } from '../../store/index.js';
import { getCartsFromDigitisationId } from '../pharmacy-orders/UploadRxComponents/Services';

const BRAND = '#6C5DD3';

const fieldStyle = {
    width: '100%', border: '1px solid #d1d5db', borderRadius: 6,
    padding: '9px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
    color: '#111827', background: '#fff',
};

// Defined at module level — prevents focus loss on re-render
function Field({ label, value, onChange, type = 'text', placeholder = '', hint, readOnly = false }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={readOnly ? undefined : e => onChange(e.target.value)}
                readOnly={readOnly}
                placeholder={placeholder}
                style={{
                    ...fieldStyle,
                    ...(readOnly && { background: '#f3f4f6', color: '#6b7280', cursor: 'default', borderColor: '#e5e7eb' }),
                }}
            />
            {hint && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{hint}</div>}
        </div>
    );
}

const STEPS = [
    { title: 'Generate Cart',        instruction: 'Confirm the digitisation request ID and create a cart for this order.' },
    { title: 'Save Pharmacy Store',  instruction: 'Enter the pharmacy location details. This store will be linked to the order.' },
    { title: 'Add UPI Beneficiary',  instruction: 'Scan the pharmacy QR code to get their UPI VPA, then register it for payout.' },
    { title: 'Place Order',          instruction: 'Enter the order amount and place the Scan & Pay order.' },
    { title: 'Upload Invoice',       instruction: 'Upload the pharmacy invoice (PDF or image) to complete the order.' },
];

export default function ScanAndPayUserFlowModal({
    cartId: initCartId,
    digitisationRequestId: initDrId,
    storeId: initStoreId,
    storeName: initStoreName,
    storeAddress: initStoreAddress,
    storePincode: initStorePincode,
    storeLatitude: initStoreLat,
    storeLongitude: initStoreLng,
    storeState: initStoreState,
    storeCity: initStoreCity,
    googlePlaceId: initGooglePlaceId,
    onClose,
}) {
    const hasStore = initStoreId != null;
    const hasCart  = initCartId  != null;

    const initStep = hasStore ? 2 : hasCart ? 1 : 0;
    const initDone = Array(5).fill(false).map((_, i) => {
        if (hasStore && i <= 1) return true;
        if (hasCart  && i === 0) return true;
        return false;
    });

    const [step, setStep] = useState(initStep);
    const [done, setDone] = useState(initDone);

    const [drId,    setDrId]    = useState(initDrId    != null ? String(initDrId)    : '');
    const [cartId,  setCartId]  = useState(initCartId  != null ? String(initCartId)  : '');
    const [storeId, setStoreId] = useState(initStoreId != null ? String(initStoreId) : '');
    const [orderId, setOrderId] = useState('');

    const [s4, setS4] = useState({
        name:          initStoreName     ?? '',
        address:       initStoreAddress  ?? '',
        pincode:       initStorePincode  ?? '',
        lat:           initStoreLat      != null ? String(initStoreLat)  : '',
        lng:           initStoreLng      != null ? String(initStoreLng)  : '',
        state:         initStoreState    ?? '',
        city:          initStoreCity     ?? '',
        googlePlaceId: initGooglePlaceId ?? '',
    });

    const [s6Name,    setS6Name]    = useState(initStoreName    ?? '');
    const [s6Vpa,     setS6Vpa]     = useState('');
    const [s6Address, setS6Address] = useState(initStoreAddress ?? '');

    const [s7Amount,  setS7Amount]  = useState('');
    const [s7Remarks, setS7Remarks] = useState('');
    const [s7Lat,     setS7Lat]     = useState('');
    const [s7Lng,     setS7Lng]     = useState('');

    const [maxAmount, setMaxAmount] = useState(null);

    const fileInputRef = useRef(null);

    const [loadings,  setLoadings]  = useState({});
    const [errors,    setErrors]    = useState({});
    const [successes, setSuccesses] = useState({});

    const getHeaders = () => {
        const state = store.getState();
        return state.user.token ? { Authorization: state.user.token } : {};
    };

    const base = config.pharmacyService;

    const markDone    = (idx) => setDone(prev => { const n = [...prev]; n[idx] = true; return n; });
    const setLoad     = (key, val) => setLoadings(prev => ({ ...prev, [key]: val }));
    const setErr      = (key, val) => setErrors(prev => ({ ...prev, [key]: val }));
    const setSuccess  = (key, val) => setSuccesses(prev => ({ ...prev, [key]: val }));

    const resetFlow = () => {
        setCartId('');
        setStoreId('');
        setOrderId('');
        setStep(0);
        setDone(Array(5).fill(false));
        setS4({ name: '', address: '', pincode: '', lat: '', lng: '', state: '', city: '', googlePlaceId: '' });
        setS6Name('');
        setS6Vpa('');
        setS6Address('');
        setS7Amount('');
        setS7Remarks('');
        setS7Lat('');
        setS7Lng('');
        setLoadings({});
        setErrors({});
        setSuccesses({});
        setMaxAmount(null);
    };

    useEffect(() => {
        if (!cartId) { setMaxAmount(null); return; }
        let cancelled = false;
        axios.get(`${base}/scan-and-pay/fetch-cart-detail?cartId=${cartId}&isSecureDocumentUrl=1`, { headers: getHeaders() })
            .then(({ data }) => {
                if (!cancelled && data.success && data.cart) {
                    const amt = data.cart.maxAmount ?? data.cart.approvedAmount ?? null;
                    setMaxAmount(amt);
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [cartId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-trigger generate-cart on open whenever a DR ID is provided
    useEffect(() => {
        if (initDrId != null) callStep0();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Step 0: Generate Cart ─────────────────────────────────────────────────
    const callStep0 = async () => {
        setErr('s0', ''); setLoad('s0', true);
        try {
            await getCartsFromDigitisationId(drId);
            const { data } = await axios.post(`${base}/scan-and-pay/generate-cart`,
                { digitisationRequestId: Number(drId) },
                { headers: getHeaders() }
            );
            if (data.success) {
                const cid = data.cart?.id ?? data.cart?.cartId ?? data.cartId;
                if (cid) setCartId(String(cid));
                setSuccess('s0', { cartId: cid });
                markDone(0);
            } else {
                setErr('s0', data.message || 'Could not generate cart. Please check the request ID and try again.');
            }
        } catch (e) {
            setErr('s0', e.response?.data?.message || 'Something went wrong. Please try again.');
        }
        setLoad('s0', false);
    };

    // ── Step 1: Save Pharmacy Store ───────────────────────────────────────────
    const saveStore = async () => {
        setErr('s1', ''); setLoad('s1', true);
        try {
            const { data } = await axios.post(`${base}/scan-and-pay/save-local-pharmacy-store`, {
                digitisationRequestId: Number(drId),
                storeDetail: {
                    storeName:      s4.name,
                    storeAddress:   s4.address,
                    storePincode:   s4.pincode,
                    storeLatitude:  parseFloat(s4.lat),
                    storeLongitude: parseFloat(s4.lng),
                    storeState:     s4.state,
                    storeCity:      s4.city,
                    ...(s4.googlePlaceId && { googlePlaceId: s4.googlePlaceId }),
                },
            }, { headers: getHeaders() });
            if (data.success) {
                const sid = data.store?.id ?? data.storeId;
                if (sid) setStoreId(String(sid));
                setS6Name(s4.name);
                setS6Address(s4.address);
                setSuccess('s1', { storeId: sid, name: s4.name });
                markDone(1);
            } else {
                setErr('s1', data.message || 'Could not save store. Please check the details and try again.');
            }
        } catch (e) {
            setErr('s1', e.response?.data?.message || 'Something went wrong. Please try again.');
        }
        setLoad('s1', false);
    };

    // ── Step 2: Add Beneficiary ───────────────────────────────────────────────
    const callStep2 = async () => {
        setErr('s2', ''); setLoad('s2', true);
        try {
            const { data } = await axios.post(`${base}/scan-and-pay/add-beneficiary`, {
                cartId:  Number(cartId),
                name:    s6Name,
                vpa:     s6Vpa,
                address: s6Address,
            }, { headers: getHeaders() });
            if (data.success) {
                setSuccess('s2', { name: s6Name, vpa: s6Vpa });
                markDone(2);
            } else {
                setErr('s2', data.message || 'Could not register beneficiary. Please verify the UPI VPA and try again.');
            }
        } catch (e) {
            setErr('s2', e.response?.data?.message || 'Something went wrong. Please try again.');
        }
        setLoad('s2', false);
    };

    // ── Step 3: Place Order ───────────────────────────────────────────────────
    const callStep3 = async () => {
        setErr('s3', ''); setLoad('s3', true);
        try {
            const body = { cartId: Number(cartId), storeId: Number(storeId), upiAmount: parseFloat(s7Amount) };
            if (s7Remarks) body.upiRemarks    = s7Remarks;
            if (s7Lat)     body.userLatitude  = parseFloat(s7Lat);
            if (s7Lng)     body.userLongitude = parseFloat(s7Lng);
            const { data } = await axios.post(`${base}/scan-and-pay/place-order`, body, { headers: getHeaders() });
            if (data.success) {
                const oid = data.order?.id ?? data.order?.orderId ?? data.orderId;
                if (oid) setOrderId(String(oid));
                setSuccess('s3', { orderId: oid, amount: s7Amount });
                markDone(3);
            } else {
                setErr('s3', data.message || 'Could not place order. Please verify the details and try again.');
            }
        } catch (e) {
            setErr('s3', e.response?.data?.message || 'Something went wrong. Please try again.');
        }
        setLoad('s3', false);
    };

    // ── Step 4: Upload Invoice ────────────────────────────────────────────────
    const callStep4 = async () => {
        setErr('s4', '');
        const files = fileInputRef.current?.files;
        if (!files?.length) { setErr('s4', 'Please select at least one invoice file before uploading.'); return; }
        setLoad('s4', true);
        try {
            const formData = new FormData();
            formData.append('orderId', orderId);
            Array.from(files).slice(0, 4).forEach(f => formData.append('files', f));
            const { data } = await axios.post(`${base}/scan-and-pay/upload-invoice`, formData, { headers: getHeaders() });
            if (data.success) {
                setSuccess('s4', { orderId, fileCount: Math.min(files.length, 4) });
                markDone(4);
            } else {
                setErr('s4', data.message || 'Invoice upload failed. Please try again.');
            }
        } catch (e) {
            setErr('s4', e.response?.data?.message || 'Something went wrong. Please try again.');
        }
        setLoad('s4', false);
    };

    // ── UI helpers ─────────────────────────────────────────────────────────────

    const ActionButton = ({ onClick, isLoading, label }) => (
        <button
            onClick={onClick}
            disabled={isLoading}
            style={{
                width: '100%', marginTop: 6, padding: '11px 16px',
                background: isLoading ? '#c4b5fd' : BRAND, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                letterSpacing: '0.1px',
            }}
        >
            {isLoading && <span className="spinner-border spinner-border-sm" role="status" />}
            {isLoading ? 'Please wait…' : label}
        </button>
    );

    const ErrorBanner = ({ msg }) => !msg ? null : (
        <div style={{ marginTop: 12, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#b91c1c', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0 }}>⚠</span>
            <span>{msg}</span>
        </div>
    );

    const SuccessBanner = ({ children }) => (
        <div style={{ marginTop: 14, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 13, color: '#15803d', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>✓</span>
            <div>{children}</div>
        </div>
    );

    // ── Step content ───────────────────────────────────────────────────────────

    const renderStepContent = () => {
        switch (step) {

            case 0:
                return (
                    <>
                        <Field label="Digitisation Request ID" value={drId} onChange={v => { setDrId(v); resetFlow(); }} type="number" placeholder="e.g. 10234" />
                        {!done[0] && <ActionButton onClick={callStep0} isLoading={loadings.s0} label="Generate Cart" />}
                        <ErrorBanner msg={errors.s0} />
                        {successes.s0 && (
                            <SuccessBanner>
                                Cart <strong>#{successes.s0.cartId}</strong> created and linked to DR #{drId}.
                            </SuccessBanner>
                        )}
                    </>
                );

            case 1:
                return (
                    <>
                        <Field label="Digitisation Request ID" value={drId} onChange={setDrId} type="number" readOnly />
                        {[
                            ['Pharmacy Name',    s4.name,          'name',          'text',   'Apollo Pharmacy Andheri'],
                            ['Address',          s4.address,       'address',       'text',   '123 Main St, Andheri West'],
                            ['Pincode',          s4.pincode,       'pincode',       'text',   '400001'],
                            ['Latitude',         s4.lat,           'lat',           'number', '19.0760'],
                            ['Longitude',        s4.lng,           'lng',           'number', '72.8777'],
                            ['State',            s4.state,         'state',         'text',   'Maharashtra'],
                            ['City',             s4.city,          'city',          'text',   'Mumbai'],
                            ['Google Place ID',  s4.googlePlaceId, 'googlePlaceId', 'text',   'ChIJ...'],
                        ].map(([label, val, key, type, ph]) => (
                            <Field key={key} label={label} value={val} type={type} placeholder={ph}
                                onChange={v => setS4(prev => ({ ...prev, [key]: v }))} />
                        ))}
                        {!done[1] && (
                            <ActionButton onClick={saveStore} isLoading={loadings.s1} label="Save Pharmacy Store" />
                        )}
                        <ErrorBanner msg={errors.s1} />
                        {successes.s1 && (
                            <SuccessBanner>
                                <strong>{successes.s1.name}</strong> registered as Store #{successes.s1.storeId}.
                            </SuccessBanner>
                        )}
                    </>
                );

            case 2:
                return (
                    <>
                        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                            Ask the pharmacy to show their UPI QR code. Scan it or ask for the VPA directly.
                        </div>
                        <Field label="Cart ID" value={cartId} onChange={setCartId} type="number" readOnly />
                        <Field label="Pharmacy Name" value={s6Name} onChange={setS6Name} placeholder="Apollo Pharmacy Andheri" />
                        <Field label="UPI VPA" value={s6Vpa} onChange={setS6Vpa} placeholder="apolloandheri@upi" hint="Scan the pharmacy QR code to get this" />
                        <Field label="Pharmacy Address" value={s6Address} onChange={setS6Address} placeholder="123 Main St, Mumbai" />
                        {!done[2] && <ActionButton onClick={callStep2} isLoading={loadings.s2} label="Register Beneficiary" />}
                        <ErrorBanner msg={errors.s2} />
                        {successes.s2 && (
                            <SuccessBanner>
                                <strong>{successes.s2.name}</strong> ({successes.s2.vpa}) registered for payout.
                            </SuccessBanner>
                        )}
                    </>
                );

            case 3:
                return (
                    <>
                        <Field label="Cart ID" value={cartId} onChange={setCartId} type="number" readOnly />
                        <Field label="Store ID" value={storeId} onChange={setStoreId} type="number" readOnly />
                        {maxAmount != null && (
                            <div style={{ marginBottom: 10, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                                Max allowed amount: <strong>₹{maxAmount}</strong>
                            </div>
                        )}
                        <Field label="Amount to Pay (₹)" value={s7Amount} onChange={setS7Amount} type="number" placeholder="500" hint={maxAmount != null ? `Max: ₹${maxAmount}` : undefined} />
                        {!done[3] && <ActionButton onClick={callStep3} isLoading={loadings.s3} label="Place Order" />}
                        <ErrorBanner msg={errors.s3} />
                        {successes.s3 && (
                            <SuccessBanner>
                                Order <strong>#{successes.s3.orderId}</strong> placed — ₹{successes.s3.amount} UPI payment initiated.
                                {successes.s3.utr && (
                                    <div style={{ marginTop: '4px', fontSize: '12px' }}>
                                        UTR: <strong>{successes.s3.utr}</strong>
                                    </div>
                                )}
                            </SuccessBanner>
                        )}
                    </>
                );

            case 4:
                return (
                    <>
                        <Field label="Order ID" value={orderId} onChange={setOrderId} type="number" readOnly />
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>
                                Invoice Files
                            </label>
                            <div style={{ border: '2px dashed #d1d5db', borderRadius: 8, padding: '20px 16px', textAlign: 'center', background: '#fafafa' }}>
                                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>PDF, JPG or PNG — up to 4 files</div>
                                <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style={{ fontSize: 13 }} />
                            </div>
                        </div>
                        {!done[4] && <ActionButton onClick={callStep4} isLoading={loadings.s4} label="Upload Invoice" />}
                        <ErrorBanner msg={errors.s4} />
                        {successes.s4 && (
                            <div style={{ marginTop: 20, padding: '20px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, textAlign: 'center' }}>
                                <div style={{ fontSize: 32, marginBottom: 6 }}>✓</div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: '#15803d' }}>Order Complete</div>
                                <div style={{ fontSize: 13, color: '#166534', marginTop: 6 }}>
                                    Invoice uploaded · Order <strong>#{successes.s4.orderId}</strong> is fully processed.
                                </div>
                            </div>
                        )}
                    </>
                );

            default:
                return null;
        }
    };

    // ── ID summary strip ───────────────────────────────────────────────────────

    const IdChip = ({ label, value }) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: value ? '#111827' : '#d1d5db', marginTop: 1 }}>
                {value ? `#${value}` : '—'}
            </span>
        </div>
    );

    // ── Stepper ────────────────────────────────────────────────────────────────

    const isLastStep = step === STEPS.length - 1;
    const canNext    = done[step];

    return (
        <Modal isOpen toggle={onClose} style={{ maxWidth: 680, width: '95vw' }}>
            <ModalHeader toggle={onClose} style={{ background: BRAND, padding: '12px 20px', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>Scan &amp; Pay — Process Order</span>
            </ModalHeader>

            <ModalBody style={{ padding: 0, maxHeight: '88vh', overflowY: 'auto' }}>

                {/* ID Summary Strip */}
                <div style={{ display: 'flex', justifyContent: 'space-around', padding: '12px 20px', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
                    <IdChip label="DR"    value={drId} />
                    <div style={{ width: 1, background: '#e5e7eb' }} />
                    <IdChip label="Cart"  value={cartId} />
                    <div style={{ width: 1, background: '#e5e7eb' }} />
                    <IdChip label="Store" value={storeId} />
                    <div style={{ width: 1, background: '#e5e7eb' }} />
                    <IdChip label="Order" value={orderId} />
                </div>

                {/* Step Tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', overflowX: 'auto' }}>
                    {STEPS.map((s, i) => {
                        const isCurrent  = i === step;
                        const isComplete = done[i];
                        const isAccessible = isComplete || i <= step;
                        return (
                            <button
                                key={i}
                                onClick={() => { if (isAccessible) setStep(i); }}
                                style={{
                                    flex: '1 0 auto',
                                    padding: '10px 6px',
                                    border: 'none',
                                    borderBottom: isCurrent ? `2px solid ${BRAND}` : '2px solid transparent',
                                    background: 'transparent',
                                    cursor: isAccessible ? 'pointer' : 'default',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 4,
                                    minWidth: 80,
                                }}
                            >
                                <span style={{
                                    width: 22, height: 22, borderRadius: '50%', fontSize: 11, fontWeight: 700,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: isComplete ? '#22c55e' : isCurrent ? BRAND : '#e5e7eb',
                                    color: isComplete || isCurrent ? '#fff' : '#9ca3af',
                                }}>
                                    {isComplete ? '✓' : i + 1}
                                </span>
                                <span style={{ fontSize: 10, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? BRAND : isComplete ? '#16a34a' : '#9ca3af', textAlign: 'center', lineHeight: '1.3', whiteSpace: 'nowrap' }}>
                                    {s.title}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Step Body */}
                <div style={{ padding: '20px 24px' }}>

                    {/* Instruction */}
                    <div style={{ marginBottom: 18, padding: '10px 14px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, fontSize: 13, color: '#5b21b6' }}>
                        {STEPS[step].instruction}
                    </div>

                    {renderStepContent()}

                    {/* Navigation */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
                        <button
                            onClick={() => setStep(s => Math.max(s - 1, 0))}
                            disabled={step === 0}
                            style={{
                                padding: '9px 20px', border: '1px solid #e5e7eb', borderRadius: 8,
                                fontSize: 13, fontWeight: 600, background: '#fff',
                                color: step === 0 ? '#d1d5db' : '#374151',
                                cursor: step === 0 ? 'not-allowed' : 'pointer',
                            }}
                        >
                            ← Back
                        </button>
                        {!isLastStep && (
                            <button
                                onClick={() => { if (canNext) setStep(s => s + 1); }}
                                disabled={!canNext}
                                style={{
                                    padding: '9px 24px', border: 'none', borderRadius: 8,
                                    fontSize: 13, fontWeight: 600, color: '#fff',
                                    background: canNext ? BRAND : '#c4b5fd',
                                    cursor: canNext ? 'pointer' : 'not-allowed',
                                }}
                            >
                                Continue →
                            </button>
                        )}
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}
